import { useSyncExternalStore } from 'react'

/**
 * The Custom View's right-hand panel: MortaAI or the page's versions. Opened from the view row's MortaAI and
 * Versions buttons, the page itself, and the view tab's Versions, so it lives outside them all in a tiny store they
 * share. Only MortaAI writes a page: there is no HTML editor.
 */
export type CustomViewPanelMode = 'ai' | 'versions'

interface PanelState {
  open: boolean
  mode: CustomViewPanelMode
  /** MortaAI is answering in the panel, so the view's tab and the panel header show a spinner. */
  working: boolean
  /** A table owner is on the Custom View's editor, where the pencil (or a new view's prompt) is MortaAI's way in,
   * so the floating one steps aside rather than offering a second chat that doesn't carry the view's thread. */
  editing: boolean
}

let state: PanelState = {
  open: false,
  mode: 'ai',
  working: false,
  editing: false
}
const listeners = new Set<() => void>()

const set = (next: PanelState) => {
  if (
    next.open === state.open &&
    next.mode === state.mode &&
    next.working === state.working &&
    next.editing === state.editing
  )
    return
  state = next
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Opens the panel in its mode. */
export const openCustomViewPanel = (mode: CustomViewPanelMode = 'ai') => set({ ...state, open: true, mode })
export const closeCustomViewPanel = () => set({ ...state, open: false, working: false })
export const setCustomViewWorking = (working: boolean) => set({ ...state, working })
export const setCustomViewEditing = (editing: boolean) => set({ ...state, editing })

/** A toolbar button: opens the panel in its mode, or closes the panel if that mode is already showing. */
export const toggleCustomViewPanel = (mode: CustomViewPanelMode) =>
  state.open && state.mode === mode ? closeCustomViewPanel() : openCustomViewPanel(mode)

export const useCustomViewPanel = (): PanelState => useSyncExternalStore(subscribe, () => state)

// The panel's width, dragged from its left edge and kept per browser.
const WIDTH_KEY = 'morta.customViewPanel.width'
export const PANEL_MIN_WIDTH = 320
export const PANEL_MAX_WIDTH = 760
const PANEL_DEFAULT_WIDTH = 420

// Never more than 45% of the window, so the page beside it keeps the larger share, but never under the minimum:
// on a narrow window the panel keeps its minimum rather than becoming unusable.
export const clampPanelWidth = (width: number) => {
  const max = Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, window.innerWidth * 0.45))
  return Math.round(Math.min(Math.max(width, PANEL_MIN_WIDTH), max))
}

export const readPanelWidth = (): number => {
  try {
    const stored = Number(localStorage.getItem(WIDTH_KEY))
    return stored > 0 ? stored : PANEL_DEFAULT_WIDTH
  } catch {
    return PANEL_DEFAULT_WIDTH
  }
}

export const writePanelWidth = (width: number) => {
  try {
    localStorage.setItem(WIDTH_KEY, String(width))
  } catch {
    // Private windows and blocked storage just don't remember the width.
  }
}
